import { Injectable } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { WindowService } from '../../../../common/angular-shared/services/window/window.service';

import * as auth0 from 'auth0-js';
import { Auth0Controller, AuthenticationInformation } from '../../../../common/js/authentication/Auth0Controller';
import { AuthenticationService } from './authentication.service';
import { Auth0Error } from '../../../../common/js/authentication/Auth0Client';
import { addPrefixForCode } from '../../../../common/js/authentication/Auth0CodeUtils';
import { InviteErrors } from '../../../js/interfaces/CoverpathTypes';

export enum AuthenticationType {
  LOG_IN = 'login',
  SIGN_UP = 'signup',
}

export enum CommonRoutes {
  HOME = '',
  ACCOUNT_CENTER = 'account-center',
  APPLICATION_FORM = 'application-form',
  HELP = 'help',
  DRAFT = 'draft-in-progress',
  INVALID_EMAIL = 'invalid-email-for-quote',
}

@Injectable()
export class Auth0Service extends AuthenticationService {
  public constructor(
    protected windowService: WindowService,
    protected route: ActivatedRoute,
    protected router: Router
  ) {
    super(windowService, route, router);
  }

  public getController(): Auth0Controller {
    return this.windowService.nativeWindow.controller;
  }

  public async goToLogin(): Promise<void> {
    this.authorize(AuthenticationType.LOG_IN);
  }

  public async login(): Promise<void> {
    throw Error('Unable to login directly when Auth0 is enabled');
  }

  public async goToRegister(): Promise<void> {
    this.signUp();
  }

  public async register(): Promise<void> {
    throw Error('Unable to register directly when Auth0 is enabled');
  }

  public async signUp(): Promise<void> {
    await this.getController().publicSave();
    await this.authorize(AuthenticationType.SIGN_UP, this.getAuth0SignUpClient());
  }

  public goToAuthenticate(): void {}

  /**
   * Authenticate using Auth0 hosted login
   * @param {AuthenticationType} authenticationType
   * @return {Promise<void>}
   */
  protected async authorize(authenticationType: AuthenticationType, auth0Client?: Auth0Client): Promise<void> {
    if (!auth0Client) {
      auth0Client = this.getAuth0Client();
    }

    await this.getController().setRedirectUri(this.windowService.nativeWindow.location.origin);
    await auth0Client.authorize(this.getAuth0AuthorizeConfig(authenticationType));
  }

  protected getAuth0Client(): Auth0Client {
    return new auth0.WebAuth(this.getWebAuthConfig(false));
  }

  protected getAuth0SignUpClient(): Auth0Client {
    return this.getAuth0Client();
  }

  protected getAuth0Callback(isSignUp: boolean): string {
    return this.windowService.nativeWindow.location.origin + '/auth0callback';
  }

  protected getWebAuthConfig(isSignUp: boolean): Auth0WebAuthConfig {
    const clientId = this.getController().auth0clientId;

    return {
      clientID: clientId,
      domain: this.getController().auth0CustomDomain,
      responseType: this.getController().auth0responseType,
      redirectUri: this.getAuth0Callback(isSignUp),
      scope: this.getController().auth0scope,
      connection: this.getController().auth0connection,
      audience: this.getController().auth0audience,
    };
  }

  protected getAuth0AuthorizeConfig(authenticationType: AuthenticationType): Auth0AuthorizeCallConfig {
    const url: string = this.windowService.nativeWindow.location.toString();
    const loginHint: string = this.getLoginHint(url, this.getController());

    return {
      mode: authenticationType,
      login_hint: loginHint,
      prompt: 'login',
      connection: this.getController().auth0connection,
      errorMessage: this.getController().auth0errorMessage,
    };
  }

  getLoginHint(url: string, controller: Auth0Controller): string {
    const queryParams: Map<string, string> = this.readQueryParams(url);
    return queryParams.has('message') ? queryParams.get('message') : controller.getAuth0LoginHint();
  }

  readQueryParams(url: string): Map<string, string> {
    const map: Map<string, string> = new Map<string, string>();
    if (url && url.indexOf('?') > -1) {
      const keyValStrings: string[] = url.split('?')[1].split('&');
      keyValStrings.forEach((s) => {
        const queryParam: string[] = s.split('=');
        map.set(queryParam[0], queryParam[1]);
      });
    }
    return map;
  }

  /**
   * Handle successful authentication from Auth0
   */
  public async handleAuthentication(client?: string): Promise<void> {
    let code = this.router.routerState.snapshot.root.queryParamMap.get('code') || '';
    code = addPrefixForCode(code);
    await this.handleAuthResult({ code });
  }

  /**
   * An Auth0 specific signature.  Analyzes the result Auth0 calls after authentication.
   * @param authResult
   */
  private async handleAuthResult(authResult: Auth0AuthorizeResponse): Promise<void> {
    try {
      await this.getController().setRedirectUri(this.windowService.nativeWindow.location.origin);

      const authInfo: AuthenticationInformation = await this.getController().establishSession(
        undefined,
        authResult.code
      );

      await this.getController().prepareSessionData(authInfo);

      await this.runPostAuthSteps(authInfo);

      await this.runClientPostProcesses();

      await this.navigateOnSuccess();
    } catch (err) {
      await this.navigateOnError(err);
    }
  }

  protected async runPostAuthSteps(authInfo: AuthenticationInformation): Promise<void> {
    if (!authInfo.isExistingUser) {
    }
    // await this.fireMarketingEvents(authInfo);
  }

  protected async runClientPostProcesses() {
    // this.getController().customer.clientInit();
    // this.legacyAuthSvc.loggedInSource.next(true);
  }

  public async navigateOnSuccess(): Promise<void> {
    const route = await this.establishRoute();
    await this.router.navigate([route]);
  }

  public async navigateOnError(error?): Promise<void> {
    let navigateTo = CommonRoutes.HOME;
    if (error.code === 'no_associated_advisor') {
      navigateTo = CommonRoutes.HELP;
    } else if (error.code === 'draft_in_progress') {
      navigateTo = CommonRoutes.DRAFT;
    } else if (error.code === InviteErrors.AAA_QUOTE_EMAIL_MISMATCH) {
      navigateTo = CommonRoutes.INVALID_EMAIL;
    }
    await this.router.navigate([navigateTo]);
  }

  protected async establishRoute(): Promise<string> {
    // TODO: dynamic - this or account center
    return CommonRoutes.APPLICATION_FORM;
  }

  public resetPassword(url: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const webAuth = new auth0.WebAuth({
        domain: this.getController().auth0domain,
        clientID: this.getController().auth0clientId,
      });

      return webAuth.changePassword(
        {
          connection: this.getController().auth0connection,
          email: this.getController().email,
        },
        (err, resp) => {
          if (err) {
            reject(err.msg);
          } else {
            resolve(resp);
          }
        }
      );
    });
  }

  public async logout(): Promise<void> {
    this.router.navigate(['logout']);
  }

  public async logoutWithStatus(statusType: string): Promise<void> {
    // redirect home and trigger query params
    await this.router.navigate([''], { queryParams: { status: statusType } });
    return this.getController().logout();
  }

  public async changeEmail(email: string, password: string): Promise<void> {
    this.getController().newEmail = email;
    this.getController().password = password;
    await this.getController().changeEmail('', document.location.href);
  }

  public async loginWithNewEmail(email: string, password: string): Promise<void> {
    await this.getController().newPublicLoginWithNewEmail(email, password);

    await this.runClientPostProcesses();
    return this.navigateOnSuccess();
  }

  public async resendVerificationEmail(): Promise<void> {
    await this.getController().resendAccountVerificationEmail();
  }
}

export interface Auth0AuthorizeResponse {
  code: string;
}

export interface Auth0Client {
  authorize(configuration);

  parseHash(handlerFunction);

  logout();
}

export interface Auth0WebAuthConfig {
  clientID: string;
  domain: string;
  responseType: string;
  redirectUri: string;
  scope: string;
  connection: string;
  audience: string;
}

export interface Auth0AuthorizeCallConfig {
  mode: string;
  login_hint: string;
  prompt: string;
  connection: string;
  errorMessage: Auth0Error;
}
